import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { MaterialModule } from '../../material/material.module';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { LanguageSelectorService } from '../../services/language-selector/language-selector.service';
import { Languages } from '../../models/util/languages';
import { CommonModule } from '@angular/common';
import { PipesModule } from '../../pipes/pipes.module';
import { CustomFilter } from '../../models/customFilter';
import { SharedModule } from '../shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-filters-modal',
	standalone: true,
	imports: [SharedModule, MaterialModule, MatIcon, MatIconButton, LanguageSelectorComponent, PipesModule],
	templateUrl: './filters-modal.component.html',
	styleUrl: './filters-modal.component.scss'
})
export class FiltersModalComponent implements OnInit {

	languagesList: Languages[] = [];
	languageSelected: Languages[] = [];
	publishStatus = PublishStatusEnum
	opened: boolean = false;
	showAppliedFiltersCount: boolean = false;

	private categoryLanguage = inject(LanguageSelectorService);
	private translateService = inject(TranslateService);

	@Input() filterByLanguages: boolean = false;
	@Input() filterByPublishStatus: boolean = false;
	@Input() filterByDate: boolean = false;
	@Input() customFilters: CustomFilter[] = [];

	@Output() filtersApplied = new EventEmitter<CustomFilter[]>();
	@Output() dateRangeSelected = new EventEmitter<{ startDate: Date | null, endDate: Date | null }>();
	@Output() languagesSelected = new EventEmitter<Languages[]>();

	dateRange: { startDate: Date | null, endDate: Date | null } = {
		startDate: null,
		endDate: null
	};

	ngOnInit(): void {
		if (this.filterByLanguages) {
			this.categoryLanguage.getLanguages().subscribe(sub => {
				sub.forEach((data: any) => {
					let languages = new Languages();
					languages.name = data.name;
					languages.code = data.code;
					this.languagesList.push(languages);
				});
			});
		}
	}
	selectLanguage(item: Languages) {
		const index = this.languageSelected.indexOf(item);
		if (index === -1) {
			item.isSelected = true;
			this.languageSelected.push(item);
		} else {
			item.isSelected = false;
			this.languageSelected.splice(index, 1);
		}
	}

	spliceLanguage(item: Languages) {
		const index = this.languageSelected.findIndex(lang => lang.code === item.code);
		if (index !== -1) {
			this.languageSelected[index].isSelected = false;
			this.languageSelected.splice(index, 1);
			this.languageSelected = [...this.languageSelected];
		}
	}

	manageFilterModal(open: boolean) {
		this.opened = open;
	}

	applyFilters() {
		this.filtersApplied.emit(this.customFilters);
		this.dateRangeSelected.emit(this.dateRange);
		this.languagesSelected.emit(this.languageSelected);
		this.manageFilterModal(false);
		this.showAppliedFiltersCount = this.activeFiltersCount > 0;
	}

	get activeFiltersCount(): number {
		let count = 0;
		count += this.customFilters.filter(f => f.selectedOptions?.length).length;
		count += this.languageSelected.length > 0 ? 1 : 0;
		count += this.dateRange.startDate || this.dateRange.endDate ? 1 : 0;
		return count;
	}

	clearFilters(): void {
		this.customFilters.forEach(filter => {
			filter.selectedOptions = [];
		});
		this.languagesList.forEach(lang => lang.isSelected = false);
		this.languageSelected = [];
		this.dateRange = {
			startDate: null,
			endDate: null
		};
		this.applyFilters();
	}

	isString(value: any): value is string {
		return typeof value === 'string';
	}

	getExtraLanguagesTooltip(): string {
		return this.languageSelected
			.slice(3)
			.map(lang => this.translateService.instant(lang.name))
			.join(', ');
	}

	get extraFilterChips(): string {
		const chips = this.activeFilterChips.slice(3);
		return chips.map(chip => `${chip.label}: ${chip.value}`).join(', ');
	}

	clearExtraChips(): void {
		const extras = this.activeFilterChips.slice(3);
		extras.forEach(chip => chip.removeFn());
	}


	get activeFilterChips(): { label: string, value: string, removeFn: () => void }[] {
		const chips: { label: string, value: string, removeFn: () => void }[] = [];
		if (this.languageSelected.length) {
			this.languageSelected.forEach(lang => {
				chips.push({
					label: this.translateService.instant('general.language'),
					value: this.translateService.instant(lang.name),
					removeFn: () => this.spliceLanguage(lang)
				});
			});
		}

		this.customFilters.forEach(filter => {
			filter.selectedOptions?.forEach(option => {
				chips.push({
					label: this.translateService.instant(filter.name),
					value: this.isString(option) ? this.translateService.instant(option) : option,
					removeFn: () => {
						filter.selectedOptions = filter.selectedOptions?.filter(o => o !== option);
					}
				});
			});
		});

		if (this.dateRange.startDate || this.dateRange.endDate) {
			const start = this.dateRange.startDate ? this.dateRange.startDate.toLocaleDateString() : '';
			const end = this.dateRange.endDate ? this.dateRange.endDate.toLocaleDateString() : '';
			chips.push({
				label: this.translateService.instant('general.date_range'),
				value: `${start} - ${end}`,
				removeFn: () => {
					this.dateRange = { startDate: null, endDate: null };
				}
			});
		}

		return chips;
	}



}
